import {BenchlingExpression} from 'benchling-expressions/BenchlingExpressionTypes';
import {
  ProcessExecutionType,
  ProcessSchemaMapperType,
  ProcessFlowchartNodeDetailsType,
  ProcessClientOrigin,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import * as net from 'util/Net';
import {CreateValidationRulesParams, CreateValidationRulesResponse} from 'validation_rules/network/types';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';

export function processTaskSchemasURL(): string {
  return '/workflow-task-schemas';
}

export function createProcessTaskSchemaURL(): string {
  return '/workflow-task-schemas/create';
}

export function processTaskSchemaURL(schemaId: string): string {
  return `/workflow-task-schemas/${schemaId}`;
}

interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<BulkArchiveResponse> {
  return net.postJSONPromise({
    url: '/1/api/process-task-schemas:bulk-archive',
    data: {ids, purpose},
  });
}

interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchive(ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return net.postJSONPromise({
    url: '/1/api/process-task-schemas:bulk-unarchive',
    data: {ids},
  });
}

interface CreateProcessOutputSchemaRequest {
  prefix: string;
  schemaFields: Array<SchemaField | ClientSchemaField>;
  autopopulateFromTasks?: boolean;
}

interface ProcessFlowchartNodeConfigSpecBase<TNodeDetails> {
  clientId: string;
  nodeDetails: TNodeDetails;
}

type ProcessFlowchartNodeConfigSpec<TNodeDetails> = ProcessFlowchartNodeConfigSpecBase<TNodeDetails> &
  (TNodeDetails extends MappableNodeDetailsSpec
    ? {inboundSchemaMappers: Array<ProcessFlowchartSchemaMapperSpec>}
    : {});

export type ProcessFlowchartV2NodeConfigSpec = ProcessFlowchartNodeConfigSpec<NodeDetailsV2Spec>;

export interface ProcessFlowchartEdgeConfigSpec {
  clientId: string;
  fromNodeClientId: string;
  toNodeClientId: string;
}

export interface ProcessFlowchartConfigVersionSpec {
  templateFlowchart: ProcessFlowchartV2Spec | null;
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
  availableTaskSchemaIds: Array<string>;
  schemaMapperConfigs: Array<ProcessFlowchartSchemaMapperConfigSpec>;
}

export interface ProcessFlowchartSchemaMapperConfigSpec {
  clientId: string;
  toSchemaId: string;
  schemaFieldMapperConfigs: Array<ProcessFlowchartSchemaFieldMapperConfigSpec>;
  mapperType: ProcessSchemaMapperType;
}

export interface ProcessFlowchartSchemaFieldMapperConfigSpec {
  fromSchemaId: string;
  lookupConfig: LookupConfigSpec;
  toSchemaFieldId: string;
  resultMustBeNonNullToMap: boolean;
}

export interface ProcessFlowchartSpec<TNodeSpec> {
  clientId: string;
  nodes: Array<TNodeSpec>;
  edges: Array<ProcessFlowchartEdgeConfigSpec>;
}

export type ProcessFlowchartV2Spec = ProcessFlowchartSpec<ProcessFlowchartV2NodeConfigSpec>;

export interface ProcessFlowchartSchemaMapperSpec {
  toNodeConfigClientId: string;
  schemaMapperConfigClientId: string | null;
  schemaMapperDependencies: Array<ProcessFlowchartSchemaMapperDependencySpec>;
}

export interface ProcessFlowchartSchemaMapperDependencySpec {
  fromNodeConfigClientId: string;
}

export interface TaskNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS;
  taskSchemaId: string;
}

export interface BaseRouterFunctionSpec {
  edgeConfigClientId: string | null;
  name: string;
}

export interface RouterFunctionSpec {
  name: string;
  edgeConfigClientId: string | null;
  expressionJson: BenchlingExpression | null;
}

export interface RouterNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS;
  name: string;
  routerFunctions: Array<RouterFunctionSpec>;
}

export interface RootNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;
  taskSchemaId: string;
}

export interface OutputNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
  outputSchemaId: string;
}

export interface EndNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS;
  name: string;
}

type MappableNodeDetailsSpec = TaskNodeDetailsSpec | OutputNodeDetailsSpec;

export type NodeDetailsV1Spec = TaskNodeDetailsSpec | RouterNodeDetailsSpec;
export type NodeDetailsV2Spec =
  | NodeDetailsV1Spec
  | RootNodeDetailsSpec
  | OutputNodeDetailsSpec
  | EndNodeDetailsSpec;

interface BaseCreateProcessTaskSchemaRequest {
  name: string;
  sqlIdentifier?: string;
  taskGroupPrefix: string;
  prefix: string;
  schemaFields: Array<SchemaField | ClientSchemaField>;
  organizationId: string;
  defaultResponsibleTeamId?: string;
  defaultResponsiblePartiesIds: Array<string>;
  statusLifecycleId: string;
  processOutputSchema: CreateProcessOutputSchemaRequest | null;
  canSetAssigneeOnTaskCreation: boolean;
  defaultCreationFolderId?: string | null;
  taskGroupInstructions?: string | null;
  taskGroupInstructionsHtml?: string | null;
  isPropagateWatchersEnabled?: boolean;
  clientOrigin: ProcessClientOrigin;
}

interface ExecutionTemplate {
  templateCollectionId: string;
  templateName: string;
  sourceTemplateId: string | null;
  publishOnSchemaCreation: boolean;
}

export type CreateProcessTaskSchemaRequest = BaseCreateProcessTaskSchemaRequest &
  (
    | {
        executionType: ProcessExecutionType.DIRECT;
        canOnlyBePartOfFlowchart: boolean;
      }
    | {
        executionType: ProcessExecutionType.ENTRY;
        executionTemplate: ExecutionTemplate;
        defaultEntryExecutionFolderId?: string | null;
        canOnlyBePartOfFlowchart: boolean;
      }
    | {
        executionType: ProcessExecutionType.FLOWCHART;
      }
    | {
        executionType: ProcessExecutionType.PROCEDURE_METHOD;
      }
    | {
        executionType: ProcessExecutionType.PROCEDURE;
      }
    | {
        executionType: ProcessExecutionType.PROCEDURE_STEP;
      }
  );

export function createProcessTaskSchema(
  data: CreateProcessTaskSchemaRequest
): Promise<ProcessTaskSchemaGraphQL> {
  return net.postJSONPromise({
    url: '/1/api/process-task-schemas',
    data,
  });
}

interface UpdateProcessOutputSchemaRequest {
  prefix?: string;
  schemaFields?: Array<SchemaField | ClientSchemaField>;
  taskFinderLookupConfig?: LookupConfigSpec | null;
  autopopulateFromTasks?: boolean;
}

export function updateProcessTaskSchema(params: {
  schemaId: string;
  taskGroupPrefix?: string;
  name?: string;
  sqlIdentifier?: string;
  prefix?: string;
  defaultResponsibleTeamId?: string | null;
  defaultResponsiblePartiesIds?: Array<string> | null;
  responsiblePartyLookupConfigs?: Array<LookupConfigSpec> | null;
  schemaFields?: Array<SchemaField | ClientSchemaField>;
  processOutputSchema: UpdateProcessOutputSchemaRequest | null;
  canSetAssigneeOnTaskCreation?: boolean;
  defaultCreationFolderId?: string | null;
  defaultEntryExecutionFolderId?: string | null;
  flowchartConfig?: ProcessFlowchartConfigVersionSpec;
  taskGroupInstructions?: string | null;
  taskGroupInstructionsHtml?: string | null;
  isPropagateWatchersEnabled?: boolean;
  configEditorEventGroupId?: string | null;
  didInitialFlowchartHaveChainedRouters?: boolean;
  actionOrigin?: string | null;
}): Promise<ProcessTaskSchemaGraphQL> {
  const {schemaId, ...data} = params;
  return net.patchJSONPromise({
    url: `/1/api/process-task-schemas/${schemaId}`,
    data,
  });
}

export interface CloneProcessTaskSchemaRequest {
  processTaskSchemaId: string;
  taskGroupPrefix: string;
  name: string;
  organizationId: string;
  executionTemplate?: ExecutionTemplate;
  shouldCloneArchivedFields?: boolean;
  actionOrigin: string;
  actionMethod: string;
}

export function cloneProcessTaskSchema(
  params: CloneProcessTaskSchemaRequest
): Promise<ProcessTaskSchemaGraphQL> {
  return net.postJSONPromise({
    url: `/1/api/process-task-schemas:clone`,
    data: params,
  });
}

export function createValidationRulesForProcessTaskSchema(
  createParams: CreateValidationRulesParams
): Promise<CreateValidationRulesResponse> {
  return net.postJSONPromise({
    url: `/1/api/process-task-schemas:create-validation-rules`,
    data: createParams,
  });
}
